import React, { useCallback, useEffect, useMemo, useState } from "react";

export const FakeContext = React.createContext({});

const defaultJoinCallback = (assistance) => {
  console.log("Join performed", assistance);
};

const defaultLeaveCallback = (assistance) => {
  console.log("Leave performed", assistance);
};

const defaultMagicPasteCallback = (fields) => {
  console.log("Magic paste performed", fields);
};

const FakeProvider = ({ children }) => {
  const [assistanceHistory, setAssistanceHistory] = useState(
    JSON.parse(localStorage.getItem("ta_assistance_history")) || [],
  );
  const [joinCallback, setJoinCallback] = useState(() => defaultJoinCallback);
  const [leaveCallback, setLeaveCallback] = useState(() => defaultLeaveCallback);
  const [magicPasteCallback, setMagicPasteCallback] = useState(
    () => defaultMagicPasteCallback,
  );
  const [isCalling, setIsCalling] = useState(false);

  const addAssistanceToHistory = useCallback((assistance) => {
    setAssistanceHistory((prevState) => [...prevState, assistance]);
  }, []);

  const getAssistanceFromHistory = useCallback(
    (assistanceRequestId) => {
      return assistanceHistory.find((item) => item.id === assistanceRequestId);
    },
    [assistanceHistory],
  );

  const updateAssistanceFromHistory = useCallback((assistance) => {
    setAssistanceHistory((prevState) =>
      prevState.map((item) => (item.id === assistance.id ? assistance : item)),
    );
  }, []);

  const removeAssistanceFromHistory = useCallback((assistanceRequestId) => {
    setAssistanceHistory((prevState) =>
      prevState.filter((item) => item.id !== assistanceRequestId),
    );
  }, []);

  const removeAllAssistanceFromHistory = useCallback(() => {
    setAssistanceHistory([]);
  }, []);

  const onCallStart = useCallback(
    (assistance) => {
      const existingAssistance = getAssistanceFromHistory(
        assistance.assistance_request_id,
      );
      const driverNames = assistance?.driver_name.split(" ");
      const newAssistance = {
        beneficiaryName: assistance?.driver_name,
        licensePlate: assistance?.license_plate,
        firstName: driverNames[0],
        lastName: driverNames.slice(1).join(" "),
        phoneNumber: assistance?.driver_phone_number,
        serialNumber: assistance?.VIN,
        status: "open",
        timestamp: Date.now(),
      };

      if (existingAssistance) {
        updateAssistanceFromHistory({
          ...existingAssistance,
          ...newAssistance,
        });
      } else {
        addAssistanceToHistory({
          id: assistance.assistance_request_id,
          ...newAssistance,
        });
      }
      setIsCalling(true);
      joinCallback(assistance);
    },
    [
      addAssistanceToHistory,
      getAssistanceFromHistory,
      joinCallback,
      updateAssistanceFromHistory,
    ],
  );

  const onCallEnd = useCallback(() => {
    setIsCalling(false);
    leaveCallback();
  }, [leaveCallback]);

  const onMagicPaste = useCallback(
    (vehicle, location, assistance) => {
      magicPasteCallback(vehicle, location, assistance);
    },
    [magicPasteCallback],
  );

  useEffect(() => {
    localStorage.setItem(
      "ta_assistance_history",
      JSON.stringify(assistanceHistory),
    );
  }, [assistanceHistory]);

  const contextValue = useMemo(() => {
    return {
      addAssistanceToHistory,
      assistanceHistory,
      getAssistanceFromHistory,
      isCalling,
      onCallEnd,
      onCallStart,
      onMagicPaste,
      removeAllAssistanceFromHistory,
      removeAssistanceFromHistory,
      setJoinCallback,
      setLeaveCallback,
      setMagicPasteCallback,
      updateAssistanceFromHistory,
    };
  }, [
    addAssistanceToHistory,
    assistanceHistory,
    getAssistanceFromHistory,
    isCalling,
    onCallEnd,
    onCallStart,
    onMagicPaste,
    removeAllAssistanceFromHistory,
    removeAssistanceFromHistory,
    setJoinCallback,
    setLeaveCallback,
    setMagicPasteCallback,
    updateAssistanceFromHistory,
  ]);

  return (
    <FakeContext.Provider value={contextValue}>{children}</FakeContext.Provider>
  );
};

export default React.memo(FakeProvider);
